import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useDocuments } from '../hooks/useDocuments';
import DashboardLayout from '../components/layout/DashboardLayout';
import UploadDropzone from '../components/documents/UploadDropzone';
import DocumentList from '../components/documents/DocumentList';
import { ChevronRight, Upload, Download, Trash2 } from 'lucide-react';

export default function ProjectView() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState(new Set());

  const {
    documents,
    loading: documentsLoading,
    createDocument,
    deleteDocument,
    deletingDocuments,
  } = useDocuments(projectId);

  // Fetch project details
  useEffect(() => {
    const fetchProject = async () => {
      try {
        const projectDoc = await getDoc(doc(db, 'projects', projectId));
        if (projectDoc.exists()) {
          setProject({ id: projectDoc.id, ...projectDoc.data() });
        } else {
          console.error('Project not found');
          navigate('/dashboard');
        }
      } catch (error) {
        console.error('Error fetching project:', error);
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      fetchProject();
    }
  }, [projectId, navigate]);

  const handleUploadComplete = async (documentData) => {
    await createDocument(documentData);
    setShowUploadModal(false);
  };

  const handleDeleteDocument = async (documentId) => {
    const result = await deleteDocument(documentId);

    if (!result.success) {
      console.error('Delete failed:', result.error);
      alert('Failed to delete document. Please try again.');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedDocs.size === 0) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedDocs.size} selected ${selectedDocs.size === 1 ? 'document' : 'documents'}?`
    );

    if (!confirmed) return;

    // Delete in parallel with error aggregation
    const results = await Promise.allSettled(
      Array.from(selectedDocs).map(docId => deleteDocument(docId))
    );

    // Count failures
    const failures = results.filter(r =>
      r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.success)
    );

    if (failures.length > 0) {
      const successCount = selectedDocs.size - failures.length;
      alert(
        failures.length === selectedDocs.size
          ? `Failed to delete all ${selectedDocs.size} documents`
          : `Deleted ${successCount} of ${selectedDocs.size} documents. ${failures.length} failed.`
      );
    }

    setSelectedDocs(new Set());
  };

  // Calculate total pages
  const totalPages = documents.reduce((sum, doc) => sum + (doc.pageCount || 0), 0);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 dark:border-emerald-500 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading project...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Project not found</p>
          <Link to="/dashboard" className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 mt-4 inline-block">
            Back to Dashboard
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Breadcrumb */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <Link to="/dashboard" className="hover:text-gray-900 dark:hover:text-white">
              Projects
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900 dark:text-white font-medium">{project.name}</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex-1">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Processed {documents.filter(d => d.status === 'processed').length} of {documents.length}
              {' '}
              {documents.length === 1 ? 'file' : 'files'}
              {totalPages > 0 && (
                <>
                  {' • '}
                  {totalPages} {totalPages === 1 ? 'page' : 'pages'}
                </>
              )}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {selectedDocs.size > 0 && (
              <button
                onClick={handleDeleteSelected}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium"
              >
                <Trash2 className="w-4 h-4" />
                Delete Selected ({selectedDocs.size})
              </button>
            )}

            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-dark-bg text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition font-medium"
            >
              <Upload className="w-4 h-4" />
              Upload Files
            </button>

            <button
              className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-dark-bg text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition font-medium"
              disabled={documents.length === 0}
            >
              <Download className="w-4 h-4" />
              Downloads
            </button>
          </div>
        </div>

        {/* Documents List */}
        <div className="bg-white dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden">
          <DocumentList
            documents={documents}
            loading={documentsLoading}
            onDelete={handleDeleteDocument}
            projectId={projectId}
            selectedDocs={selectedDocs}
            onSelectionChange={setSelectedDocs}
            deletingDocuments={deletingDocuments}
          />
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-dark-border">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Upload Files</h2>
            </div>
            <div className="p-6">
              <UploadDropzone
                projectId={projectId}
                onUploadComplete={handleUploadComplete}
              />
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-dark-border flex justify-end gap-3">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </DashboardLayout>
  );
}
